import { useEffect, useRef, useState } from 'react';
import { AudioPlayer } from '../lib/audioPlayer.js';
import { PLAYER_WIDTH, SyncedPlayer } from '../lib/player.js';
import { formatDuration, SOURCE_LABEL } from '../lib/protocol.js';
import type { Member } from '../lib/protocol.js';
import type { Jam } from '../lib/useJam.js';
import { useTick } from '../lib/useTick.js';
import { loadPlaybackMode } from '../lib/playbackMode.js';
import { useWakeLock } from '../lib/useWakeLock.js';

type Props = {
  jam: Jam;
  audioOn: boolean;
  onToggleAudio: (enabled: boolean) => void;
  memberById: Map<string, Member>;
};

export function PlayerPanel({ jam, audioOn, onToggleAudio, memberById }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [drift, setDrift] = useState(0);
  const [playerError, setPlayerError] = useState<string | null>(null);

  // 再生ループはコールバックの中で常に最新の jam を読む必要があるので、
  // 依存配列に jam を入れずに ref 経由で参照する。プレイヤーを作り直すと
  // 音が途切れてしまう。
  const jamRef = useRef(jam);
  jamRef.current = jam;

  /**
   * 音を出しているのがこの端末だけなら、合わせる相手がいないので補正しない。
   * seekTo の音飛びと、setPlaybackRate による速度変更の揺れがそのまま
   * 音質の劣化として聞こえるため、必要のない場面では一切かけない。
   */
  const audioDevices =
    jam.state?.members.filter((m) => m.online && m.audio).length ?? 0;
  const soloPlayback = audioOn && audioDevices <= 1;
  const soloRef = useRef(soloPlayback);
  soloRef.current = soloPlayback;

  useTick(250);

  /**
   * 再生方式。
   *
   * 'audio' … サーバが中継する音声を `<audio>` で鳴らす。スマホで画面を消しても
   *           別アプリへ移っても鳴り続ける。既定。
   * 'iframe' … YouTube の埋め込み。音声中継が使えないときのフォールバック。
   *            バックグラウンド再生はできない。
   */
  const [mode, setMode] = useState<'checking' | 'audio' | 'iframe'>(() =>
    loadPlaybackMode() === 'iframe' ? 'iframe' : 'checking',
  );

  // 利用者が音声方式を選んでいても、サーバ側で中継が使えなければ
  // 埋め込みに落とす。黙って無音になるより、映像付きで鳴るほうがよい。
  useEffect(() => {
    if (mode !== 'checking') return;
    let cancelled = false;
    void fetch('/api/audio-support')
      .then((res) => (res.ok ? res.json() : { available: false }))
      .then((json: { available?: boolean }) => {
        if (!cancelled) setMode(json.available ? 'audio' : 'iframe');
      })
      .catch(() => {
        if (!cancelled) setMode('iframe');
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  /**
   * 音を出している端末では、再生中に画面を消させない。
   * スマホは画面が消えると YouTube の埋め込みが再生を止めるため。
   */
  const [keepScreenOn, setKeepScreenOn] = useState(() => {
    try {
      return localStorage.getItem('jam:keepScreenOn') !== 'off';
    } catch {
      return true;
    }
  });

  const np = jam.state?.nowPlaying ?? null;
  const positionMs = np
    ? np.paused
      ? np.positionMs
      : np.positionMs + (jam.serverNow() - np.startedAtMs)
    : 0;

  // 音声方式なら画面を消しても鳴り続けるので、画面を点けておく必要はない。
  // 埋め込み方式のときだけ Wake Lock を使う。
  const wakeLockNeeded = mode === 'iframe';
  const wakeLock = useWakeLock(
    wakeLockNeeded && keepScreenOn && audioOn && Boolean(np) && !np?.paused,
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!audioOn || !container || mode === 'checking') return;

    let player: { destroy(): void } | null = null;
    let cancelled = false;
    setPlayerError(null);

    // 両方式で共通の設定。方式が変わっても同期の考え方は同じ。
    const shared = {
      container,
      getTarget: () => {
        const current = jamRef.current.state?.nowPlaying;
        if (!current) return null;
        const ms = current.paused
          ? current.positionMs
          : current.positionMs + (jamRef.current.serverNow() - current.startedAtMs);
        return {
          trackId: current.track.id,
          videoId: current.track.videoId,
          targetSec: ms / 1000,
          paused: current.paused,
        };
      },
      shouldCorrect: () => !soloRef.current,
      onPosition: (trackId: string, positionMs: number) =>
        jamRef.current.actions.syncPosition(trackId, positionMs),
      onEnded: (trackId: string) => jamRef.current.actions.reportEnded(trackId),
      onUnplayable: (trackId: string, code: number) => {
        setPlayerError(`この曲は再生できませんでした (コード ${code})。次の曲に送ります。`);
        // 1曲でも詰まるとキュー全体が固まるため、終了扱いにして次へ進める。
        jamRef.current.actions.reportEnded(trackId);
      },
      onDriftChange: (value: number) => setDrift(value),
    };

    const created =
      mode === 'audio'
        ? AudioPlayer.create({
            ...shared,
            onMeta: (meta) => jamRef.current.actions.reportMeta(meta),
            getMediaInfo: () => {
              const track = jamRef.current.state?.nowPlaying?.track;
              if (!track) return null;
              return {
                title: track.title,
                artist: track.artist,
                artwork: track.thumbnail,
              };
            },
            onCommand: (command) => {
              const actions = jamRef.current.actions;
              if (command === 'play') actions.resume();
              else if (command === 'pause') actions.pause();
              else actions.skip();
            },
          })
        : SyncedPlayer.create({
            ...shared,
            onMeta: (meta) => jamRef.current.actions.reportMeta(meta),
          });

    void created
      .then((instance) => {
        if (cancelled) instance.destroy();
        else player = instance;
      })
      .catch((err: unknown) => {
        setPlayerError(
          err instanceof Error ? err.message : 'プレイヤーを起動できませんでした',
        );
      });

    return () => {
      cancelled = true;
      player?.destroy();
    };
  }, [audioOn, mode]);

  /**
   * iframe は 1280px 幅で作ってあるので、枠に収まる倍率を CSS 変数で渡す。
   * 縮めるのは見た目だけで、YouTube 側が見るサイズは 1280px のまま。
   *
   * 依存配列を置かず毎レンダリングで適用し直す。useTick が 250ms ごとに
   * 再描画するので、幅が確定していない状態から始まっても必ず追いつく。
   * ResizeObserver だけに頼ると、枠の幅が 0 のときに観測を始めた場合に
   * 倍率が入らないままになる。
   */
  useEffect(() => {
    const container = containerRef.current;
    const width = container?.clientWidth ?? 0;
    if (!container || width <= 0) return;
    container.style.setProperty('--player-scale', String(width / PLAYER_WIDTH));
  });

  const track = np?.track ?? null;
  const addedBy = track ? memberById.get(track.memberId) : undefined;
  const durationMs = track?.durationMs ?? 0;
  const progress = durationMs > 0 ? Math.min(positionMs / durationMs, 1) : 0;

  const isHost = jam.me?.id === jam.state?.hostId;
  const canControl = Boolean(
    track && (isHost || track.memberId === jam.me?.id),
  );
  const noAudioAnywhere = !jam.state?.members.some((m) => m.online && m.audio);
  /** 埋め込み方式のときだけ映像枠を見せる。音声方式では音だけ */
  const showVideo = audioOn && mode === 'iframe';

  return (
    <section className="player">
      <div className={`stage ${showVideo ? 'stage-live' : ''}`}>
        {/*
          プレイヤーの置き場所。埋め込み方式では IFrame がここに入るので
          隠すと再生が不安定になる。音声方式では `<audio>` が入るだけなので
          見せる必要がない。
        */}
        <div ref={containerRef} className="stage-frame" hidden={!showVideo} />
        {!showVideo && (
          <div className="stage-idle">
            {track?.thumbnail ? (
              <img
                src={track.thumbnail}
                alt=""
                className={`stage-art ${audioOn ? 'stage-art-clear' : ''}`}
              />
            ) : (
              <div className="stage-art stage-art-empty" />
            )}
            {!audioOn && (
              <div className="stage-idle-overlay">
                <p>この端末は音を出していません</p>
                <button className="btn btn-primary" onClick={() => onToggleAudio(true)}>
                  この端末から音を出す
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="track-info">
        {track ? (
          <>
            <div className="track-title">{track.title}</div>
            <div className="track-sub">
              {track.artist && <span>{track.artist}</span>}
              <span className={`badge badge-${track.source}`}>
                {SOURCE_LABEL[track.source]}
              </span>
            </div>
            {addedBy && (
              <div className="track-owner">
                <span className="dot" style={{ background: addedBy.color }} />
                {addedBy.name} が追加
              </div>
            )}
          </>
        ) : (
          <div className="track-title track-empty">
            キューは空です。曲を追加してください。
          </div>
        )}
      </div>

      <div className="progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <div className="progress-times">
          <span>{formatDuration(positionMs)}</span>
          <span>{durationMs > 0 ? formatDuration(durationMs) : '--:--'}</span>
        </div>
      </div>

      <div className="controls">
        <button
          className="btn"
          onClick={() => (np?.paused ? jam.actions.resume() : jam.actions.pause())}
          disabled={!np}
        >
          {np?.paused ? '再生' : '一時停止'}
        </button>
        <button
          className="btn"
          onClick={() => jam.actions.skip()}
          disabled={!canControl}
          title={
            canControl
              ? undefined
              : '自分が入れた曲かホストのみスキップできます'
          }
        >
          スキップ
        </button>
        <label className="toggle">
          <input
            type="checkbox"
            checked={audioOn}
            onChange={(e) => onToggleAudio(e.target.checked)}
          />
          <span>この端末から音を出す</span>
        </label>
      </div>

      {audioOn && wakeLockNeeded && wakeLock.supported && (
        <label className="toggle toggle-left">
          <input
            type="checkbox"
            checked={keepScreenOn}
            onChange={(e) => {
              setKeepScreenOn(e.target.checked);
              try {
                localStorage.setItem('jam:keepScreenOn', e.target.checked ? 'on' : 'off');
              } catch {
                // プライベートモードなどで保存できなくても動作には影響しない
              }
            }}
          />
          <span>
            再生中は画面を消さない
            {/*
              画面が隠れている間は OS がロックを解放するので、保持できていなくても
              異常ではない。表示中なのに取れていないときだけ注意書きを出す。
            */}
            {keepScreenOn &&
              !wakeLock.held &&
              np &&
              !np.paused &&
              document.visibilityState === 'visible' &&
              '（この端末では効きません）'}
          </span>
        </label>
      )}

      {noAudioAnywhere && np && !np.paused && (
        <p className="warn">
          音を出している端末がありません。誰かが「この端末から音を出す」を
          有効にすると鳴ります。
        </p>
      )}
      {playerError && <p className="warn">{playerError}</p>}
      {audioOn && mode === 'audio' && (
        <p className="hint hint-small">
          画面を消しても別のアプリを開いても鳴り続けます。
        </p>
      )}
      {audioOn && mode === 'iframe' && (
        <p className="hint hint-small">
          {loadPlaybackMode() === 'iframe'
            ? '動画で再生しています。スマホで画面を消すと止まります。'
            : 'この端末では音声を取得できないため、動画での再生に切り替えました。'}
        </p>
      )}
      {soloPlayback && (
        <p className="hint hint-small">
          この端末だけが音を出しているので、タイミング補正はしていません。
        </p>
      )}
      {audioOn && !soloPlayback && Math.abs(drift) >= 0.3 && (
        <p className="hint hint-small">
          同期のずれ {drift > 0 ? '+' : ''}
          {drift.toFixed(2)} 秒 — 補正中
        </p>
      )}
    </section>
  );
}
